// SPDX-License-Identifier: AGPL-3.0-or-later

type CtrlArrowLeftEvent = Pick<
	KeyboardEvent | React.KeyboardEvent<HTMLTextAreaElement>,
	'key' | 'ctrlKey' | 'altKey' | 'metaKey' | 'shiftKey' | 'defaultPrevented'
>;

function isLineBreak(char: string): boolean {
	return char === '\n' || char === '\r';
}

function isWhitespace(char: string): boolean {
	return /\s/u.test(char);
}

export function getPreviousWordBoundaryAcrossLineStart(value: string, position: number): number {
	let index = Math.max(0, Math.min(position, value.length));
	while (index > 0 && isWhitespace(value[index - 1] ?? '')) {
		index--;
	}
	while (index > 0 && !isWhitespace(value[index - 1] ?? '')) {
		index--;
	}
	return index;
}

function getFocusEdge(textarea: HTMLTextAreaElement): number {
	const start = textarea.selectionStart ?? 0;
	const end = textarea.selectionEnd ?? 0;
	if (start === end) return start;
	return textarea.selectionDirection === 'backward' ? start : end;
}

function getAnchorEdge(textarea: HTMLTextAreaElement): number {
	const start = textarea.selectionStart ?? 0;
	const end = textarea.selectionEnd ?? 0;
	if (start === end) return start;
	return textarea.selectionDirection === 'backward' ? end : start;
}

export function shouldHandleCtrlArrowLeftAcrossLineStart(
	event: CtrlArrowLeftEvent,
	textarea: HTMLTextAreaElement,
): boolean {
	if (event.defaultPrevented) return false;
	if (event.key !== 'ArrowLeft') return false;
	if (!event.ctrlKey || event.altKey || event.metaKey) return false;
	const focus = getFocusEdge(textarea);
	if (focus <= 0) return false;
	return isLineBreak(textarea.value[focus - 1] ?? '');
}

export function applyCtrlArrowLeftAcrossLineStart(
	event: CtrlArrowLeftEvent,
	textarea: HTMLTextAreaElement,
): number | null {
	const focus = getFocusEdge(textarea);
	const nextFocus = getPreviousWordBoundaryAcrossLineStart(textarea.value, focus);
	if (nextFocus === focus) return null;
	if (event.shiftKey) {
		const anchor = getAnchorEdge(textarea);
		const start = Math.min(anchor, nextFocus);
		const end = Math.max(anchor, nextFocus);
		const direction: 'forward' | 'backward' | 'none' =
			start === end ? 'none' : nextFocus < anchor ? 'backward' : 'forward';
		textarea.setSelectionRange(start, end, direction);
	} else {
		textarea.setSelectionRange(nextFocus, nextFocus);
	}
	return nextFocus;
}
